import React, { useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { formatTime, toDate, clearDriverPendingDues, reconcileDriverAccounting, setDriverDuesHold } from '../services/firebase';
import { isSameDay, RANGE_OPTIONS, rangeBounds, inBounds } from '../utils/rides';
import { downloadCsv } from '../utils/exportCsv';
import RecordPaymentModal from '../components/RecordPaymentModal';
import DuesBreakdownModal from '../components/DuesBreakdownModal';
import { buildDuesBreakdown, duesSplitLabel } from '../utils/duesBreakdown';
import {
  isDuesHeld, canHoldForDues, duesHoldConfirmText, duesHoldResultText, duesHoldErrorText,
} from '../utils/duesHold';
import { useLang } from '../lang/LangContext';

export default function Cash({ rides = [], drivers = [], driverPayments = [], isSuperAdmin = false }) {
  const { t } = useLang();
  const [busy, setBusy] = useState(null);
  const paymentInFlight = useRef(false);
  const [period, setPeriod] = useState('today'); // a RANGE_OPTIONS value
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const bounds = useMemo(() => rangeBounds(period, customFrom, customTo), [period, customFrom, customTo]);
  const [activeTab, setActiveTab] = useState('ledger'); // 'ledger' | 'receipts'
  const [ledgerFilter, setLedgerFilter] = useState('all'); // 'all' | 'pending' | 'cleared'
  const [ledgerQuery, setLedgerQuery] = useState('');
  const [receiptsQuery, setReceiptsQuery] = useState('');
  const [paymentModalDriver, setPaymentModalDriver] = useState(null);
  const [breakdownDriverId, setBreakdownDriverId] = useState(null);

  // Date period matching helper for cash rides
  const filterRidesByPeriod = useMemo(() => {
    const completedTime = (r) => toDate(r.completedAt || r.updatedAt || r.createdAt)?.getTime() || 0;
    return rides.filter((r) => {
      if (r.status !== 'completed') return false;
      const d = toDate(r.completedAt || r.updatedAt || r.createdAt);
      if (!d) return false;
      return inBounds(d, bounds);
    }).sort((a, b) => completedTime(b) - completedTime(a));
  }, [rides, bounds]);

  // Breakdown by captain for selected period
  const periodData = useMemo(() => {
    const completed = filterRidesByPeriod;
    const byCaptain = {};
    completed.forEach((r) => {
      const id = r.driverId || 'unknown';
      if (!byCaptain[id]) {
        byCaptain[id] = {
          id,
          name: r.driverName || drivers.find((d) => d.id === id || d.uid === id)?.name || 'Unknown',
          vehicle: r.vehicleNumber || drivers.find((d) => d.id === id || d.uid === id)?.vehicleNumber || '—',
          rides: 0,
          fare: 0,
          tips: 0,
        };
      }
      byCaptain[id].rides += 1;
      byCaptain[id].fare += Number(r.fare) || 0;
      // Boost is already in fare. Only post-trip reported tips are separate.
      byCaptain[id].tips += r.rating ? Number(r.tip) || 0 : 0;
    });
    const rows = Object.values(byCaptain).sort((a, b) => b.fare - a.fare);
    const totalFare = rows.reduce((s, r) => s + r.fare, 0);
    const totalTips = rows.reduce((s, r) => s + r.tips, 0);
    const totalPlatformDuesOwed = drivers.reduce((s, d) => s + (Number(d.pendingDues) || 0), 0);
    return { completed, rows, totalFare, totalTips, totalPlatformDuesOwed };
  }, [filterRidesByPeriod, drivers]);

  // Captain Platform Dues Ledger. Fees come from each ride's server-written
  // driverFees (utils/duesBreakdown.js); older rides are counted, not re-priced.
  // Anti-shadowing: Lifetime rides strictly reflects the authoritative database maximum
  const ledgerData = useMemo(() => {
    const list = drivers.map((d) => {
      // Find all completed rides for this captain (in memory)
      const captainRides = rides.filter(
        (r) =>
          r.status === 'completed' &&
          (r.driverId === d.id || r.driverId === d.uid || (r.driverPhone && d.phone && r.driverPhone.replace(/\D/g, '').slice(-10) === d.phone.replace(/\D/g, '').slice(-10)))
      );

      // Authoritative non-decreasing lifetime rides: never shadowed down by memory slice
      const lifetimeRides = Math.max(
        Number(d.totalCompletedRides) || 0,
        Number(d.lifetimeRides) || 0,
        Number(d.completedRides) || 0,
        Number(d.totalRides) || 0,
        captainRides.length
      );
      const pendingDues = Math.max(0, Number(d.pendingDues) || 0);
      const breakdown = buildDuesBreakdown(d, captainRides);
      const feesCharged = breakdown.charged.total;

      // Historical payment amounts were not recorded before the new receipt
      // ledger. Do not infer them from ride count.
      const clearedDues = d.totalDuesPaid != null
        ? Number(d.totalDuesPaid)
        : null;

      const lastPaidDate = toDate(d.duesLastPaidAt);
      const lastPaidLabel = lastPaidDate ? formatTime(d.duesLastPaidAt) : '—';

      return {
        ...d,
        name: d.name || 'Captain',
        phone: d.phone || '—',
        vehicle: d.vehicleNumber || '—',
        lifetimeRides,
        feesCharged,
        breakdown,
        pendingDues,
        clearedDues,
        lastPaidLabel,
      };
    });

    const pendingCount = list.filter((d) => d.pendingDues > 0).length;
    const clearedCount = list.filter((d) => d.pendingDues === 0).length;

    const filtered = list.filter((d) => {
      if (ledgerFilter === 'pending' && d.pendingDues <= 0) return false;
      if (ledgerFilter === 'cleared' && d.pendingDues > 0) return false;
      if (ledgerQuery.trim()) {
        const q = ledgerQuery.trim().toLowerCase();
        const hay = `${d.name} ${d.phone} ${d.vehicle} ${d.id}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    }).sort((a, b) => b.pendingDues - a.pendingDues || b.feesCharged - a.feesCharged);

    return { list: filtered, all: list, pendingCount, clearedCount, totalCaptains: list.length };
  }, [drivers, rides, ledgerFilter, ledgerQuery]);

  // Real-time Receipts Data from driverPayments
  const receiptsData = useMemo(() => {
    const now = new Date();
    const todayPayments = driverPayments.filter((p) => {
      const d = toDate(p.paidAt);
      return d && isSameDay(d, now);
    });
    const todayCollected = todayPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const totalCollected = driverPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);

    const filtered = driverPayments.filter((p) => {
      if (!receiptsQuery.trim()) return true;
      const q = receiptsQuery.trim().toLowerCase();
      const hay = `${p.driverName || ''} ${p.driverPhone || ''} ${p.vehicleNumber || ''} ${p.adminEmail || ''} ${p.notes || ''} ${p.method || ''} ${p.id || ''}`.toLowerCase();
      return hay.includes(q);
    });

    return {
      list: filtered,
      todayCollected,
      totalCollected,
      todayCount: todayPayments.length,
      totalCount: driverPayments.length,
    };
  }, [driverPayments, receiptsQuery]);

  // Batch-parallel Reconcile All Drivers
  const verifyTotals = async () => {
    if (paymentInFlight.current) return;
    const unmigrated = drivers.filter((d) => d.accountingVersion !== 1);
    if (unmigrated.length === 0) {
      toast.success('All drivers already verified and up to date with true accounting history!');
      return;
    }

    paymentInFlight.current = true;
    setBusy('verify');
    const toastId = toast.loading(`Verifying 0 of ${unmigrated.length} drivers…`);
    try {
      let done = 0;
      const chunkSize = 4;
      for (let i = 0; i < unmigrated.length; i += chunkSize) {
        const chunk = unmigrated.slice(i, i + chunkSize);
        await Promise.all(
          chunk.map((driver) =>
            reconcileDriverAccounting(driver.id).catch((e) => {
              console.warn('Reconcile driver warn:', driver.id, e?.message);
            })
          )
        );
        done += chunk.length;
        toast.loading(`Verified ${done} of ${unmigrated.length} drivers…`, { id: toastId });
      }
      toast.success('Lifetime ride, fare, and dues totals verified across all drivers!', { id: toastId });
    } catch (e) {
      toast.error(e.message || 'Verification incomplete. Retry to continue.', { id: toastId });
    } finally {
      paymentInFlight.current = false;
      setBusy(null);
    }
  };

  // Dues hold (super admin): no new rides or trips until he has paid. The server decides
  // and answers with what it actually did (offline now, or after his current trip).
  const controlDuesHold = async (driver, action) => {
    if (busy !== null) return;
    if (!window.confirm(duesHoldConfirmText(driver, action))) return;
    setBusy(`hold:${driver.id}`);
    try {
      const result = await setDriverDuesHold(driver.id, action);
      toast.success(duesHoldResultText(driver, result));
    } catch (error) {
      console.error('setDriverDuesHold error:', error);
      toast.error(duesHoldErrorText(error));
    } finally {
      setBusy(null);
    }
  };

  const exportLedgerCsv = () => {
    downloadCsv(`captain-dues-ledger-${new Date().toISOString().slice(0, 10)}.csv`, [
      ['captain', 'phone', 'vehicle', 'lifetime_rides', 'fees_charged_inr', 'rides_not_itemised', 'recorded_payments_inr', 'pending_dues_inr',
        'pending_ride_fees_inr', 'pending_night_fees_inr', 'pending_cancellation_fees_inr', 'pending_earlier_balance_inr', 'last_paid'],
      ...ledgerData.list.map((d) => [
        d.name, d.phone, d.vehicle, d.lifetimeRides, d.feesCharged, d.breakdown.notItemised, d.clearedDues, d.pendingDues,
        d.breakdown.owed.rideFee, d.breakdown.owed.nightFee, d.breakdown.owed.cancellationFee, d.breakdown.owed.earlier, d.lastPaidLabel,
      ]),
    ]);
  };

  const exportReceiptsCsv = () => {
    downloadCsv(`dues-receipts-${new Date().toISOString().slice(0, 10)}.csv`, [
      ['receipt_id', 'date_time', 'captain', 'vehicle', 'phone', 'amount_inr', 'method', 'balance_before', 'balance_after', 'collected_by', 'notes'],
      ...receiptsData.list.map((p) => [
        p.id,
        formatTime(p.paidAt),
        p.driverName || 'Anna',
        p.vehicleNumber || '—',
        p.driverPhone || '—',
        p.amount,
        p.method || 'cash',
        p.balanceBefore ?? '—',
        p.balanceAfter ?? '—',
        p.adminEmail || p.adminUid || 'admin',
        p.notes || '',
      ]),
    ]);
  };

  // Custom with no dates picked shows everything, so label it honestly as all time.
  const periodLabel = !bounds
    ? 'All Time (Historic)'
    : period === 'custom'
      ? (bounds[0] === bounds[1] ? bounds[0] : `${bounds[0]} → ${bounds[1]}`)
      : (RANGE_OPTIONS.find(([v]) => v === period) || [])[1];
  const periodFileTag = !bounds ? 'all' : period === 'custom' ? `${bounds[0]}_to_${bounds[1]}` : period;

  return (
    <>
      {/* Verification Notice & Batch Sync Bar */}
      <div className="card" style={{ padding: 14, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <span style={{ fontWeight: 700, color: '#f8fafc' }}>🔒 {t('Authoritative Lifetime Accounting')}</span>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
            {t('Lifetime rides and fare totals strictly reflect true database history. Dues are collected at ₹5/completed trip (₹10 for trips booked after 10pm).')}
          </p>
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={busy !== null}
          onClick={verifyTotals}
          style={{ whiteSpace: 'nowrap' }}
        >
          {busy === 'verify' ? t('Verifying All Drivers…') : t('⚡ Reconcile All Drivers')}
        </button>
      </div>

      {/* Period Filter & High-Level Metrics */}
      <div className="toolbar" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span className="field-label" style={{ margin: 0 }}>{t('PERIOD FILTER:')}</span>
          <select value={period} onChange={(e) => setPeriod(e.target.value)} style={{ fontWeight: 600 }}>
            {RANGE_OPTIONS.map(([v, label]) => <option key={v} value={v}>{t(label)}</option>)}
          </select>
          {period === 'custom' && (
            <>
              <input type="date" style={{ minWidth: 0 }} value={customFrom} max={customTo || undefined} onChange={(e) => setCustomFrom(e.target.value)} aria-label="From date" />
              <span className="muted">→</span>
              <input type="date" style={{ minWidth: 0 }} value={customTo} min={customFrom || undefined} onChange={(e) => setCustomTo(e.target.value)} aria-label="To date" />
            </>
          )}
        </div>
        <span className="badge green" style={{ fontSize: 12, padding: '6px 12px' }}>
          Period: {periodLabel}
        </span>
      </div>

      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: 16 }}>
        <div className="stat-card green">
          <div className="stat-label">CASH RIDES ({periodLabel.toUpperCase()})</div>
          <div className="stat-value green">{periodData.completed.length}</div>
        </div>
        <div className="stat-card blue">
          <div className="stat-label">FARE COLLECTED</div>
          <div className="stat-value blue">₹{periodData.totalFare}</div>
          <div className="stat-sub">Paid directly to Anna</div>
        </div>
        <div className="stat-card orange">
          <div className="stat-label">TIPS LOGGED</div>
          <div className="stat-value orange">₹{periodData.totalTips}</div>
        </div>
        <div className="stat-card red">
          <div className="stat-label">TOTAL PENDING DUES (₹5/RIDE, ₹10 AT NIGHT)</div>
          <div className="stat-value red">₹{periodData.totalPlatformDuesOwed}</div>
          <div className="stat-sub">Owed by Annas to platform</div>
        </div>
      </div>

      {/* Primary Section Switcher: Ledger vs Receipts */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <button
          type="button"
          className={`btn ${activeTab === 'ledger' ? 'btn-green' : 'btn-ghost'}`}
          onClick={() => setActiveTab('ledger')}
          style={{ fontWeight: 700, padding: '8px 18px' }}
        >
          💰 {t('Captain Dues Ledger')} ({ledgerData.totalCaptains})
        </button>
        <button
          type="button"
          className={`btn ${activeTab === 'receipts' ? 'btn-green' : 'btn-ghost'}`}
          onClick={() => setActiveTab('receipts')}
          style={{ fontWeight: 700, padding: '8px 18px', display: 'flex', alignItems: 'center', gap: 6 }}
        >
          🧾 Recent Receipts &amp; Collections ({receiptsData.totalCount})
          {receiptsData.todayCollected > 0 && (
            <span className="badge green" style={{ fontSize: 11, padding: '2px 6px' }}>
              +₹{receiptsData.todayCollected} today
            </span>
          )}
        </button>
      </div>

      {/* TAB 1: Captain Dues Ledger */}
      {activeTab === 'ledger' && (
        <div className="card">
          <div className="card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <span>💰 Captain Platform Dues Ledger (₹5/Ride, ₹10 after 10pm)</span>
              <span className="muted" style={{ display: 'block', fontSize: 12, marginTop: 2, fontWeight: 400 }}>
                100% authoritative database history · Partial payments &amp; 1-click collections enabled
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Search captain, phone, vehicle…"
                value={ledgerQuery}
                onChange={(e) => setLedgerQuery(e.target.value)}
                style={{ padding: '7px 12px', fontSize: 13, minWidth: 200 }}
              />
              <div className="preset-pills-row" style={{ margin: 0 }}>
                <button
                  type="button"
                  className={`preset-pill ${ledgerFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setLedgerFilter('all')}
                >
                  All ({ledgerData.totalCaptains})
                </button>
                <button
                  type="button"
                  className={`preset-pill ${ledgerFilter === 'pending' ? 'active' : ''}`}
                  onClick={() => setLedgerFilter('pending')}
                >
                  Pending ({ledgerData.pendingCount})
                </button>
                <button
                  type="button"
                  className={`preset-pill ${ledgerFilter === 'cleared' ? 'active' : ''}`}
                  onClick={() => setLedgerFilter('cleared')}
                >
                  Cleared ({ledgerData.clearedCount})
                </button>
              </div>
              <button type="button" className="btn btn-ghost btn-sm" onClick={exportLedgerCsv}>
                Export Ledger CSV
              </button>
            </div>
          </div>

          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>CAPTAIN</th>
                  <th>PHONE</th>
                  <th>VEHICLE</th>
                  <th>LIFETIME RIDES</th>
                  <th>FEES CHARGED</th>
                  <th>TOTAL PAID</th>
                  <th>PENDING DUES</th>
                  <th>LAST PAID</th>
                  <th>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {ledgerData.list.map((d) => (
                  <tr key={d.id}>
                    <td><strong>{d.name}</strong></td>
                    <td className="mono">{d.phone}</td>
                    <td className="mono">{d.vehicle}</td>
                    <td className="mono"><strong>{d.lifetimeRides}</strong> rides</td>
                    <td className="mono">
                      ₹{d.feesCharged}
                      {d.breakdown.notItemised > 0 && (
                        <div
                          className="muted"
                          style={{ fontSize: 10, marginTop: 2 }}
                          title="Rides completed before 14 Oct 2026, when the server started recording each ride's fees"
                        >
                          + {d.breakdown.notItemised} older {d.breakdown.notItemised === 1 ? 'ride' : 'rides'} not itemised
                        </div>
                      )}
                    </td>
                    <td className="mono" style={{ color: '#34d399', fontWeight: 600 }}>
                      {d.clearedDues == null ? 'Unknown' : `₹${d.clearedDues}`}
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => setBreakdownDriverId(d.id)}
                        title="See what these dues are made of, ride by ride"
                        style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left', color: 'inherit', font: 'inherit' }}
                      >
                        {d.pendingDues > 0 ? (
                          <span className="badge red" style={{ fontSize: 13, fontWeight: 700 }}>₹{d.pendingDues}</span>
                        ) : (
                          <span className="badge green">₹0 Paid</span>
                        )}
                        {d.pendingDues > 0 && (
                          <span className="muted" style={{ display: 'block', fontSize: 10, marginTop: 3, textDecoration: 'underline dotted' }}>
                            {duesSplitLabel(d.breakdown.owed)}
                          </span>
                        )}
                      </button>
                      {isDuesHeld(d) && (
                        <span
                          className="badge orange"
                          style={{ marginLeft: 6 }}
                          title="No new rides or trips until he pays. It lifts by itself when a payment clears his dues."
                        >
                          ⏸ {t('Dues hold')}
                        </span>
                      )}
                    </td>
                    <td className="mono muted" style={{ fontSize: 11 }}>{d.lastPaidLabel}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        {d.pendingDues > 0 ? (
                          isSuperAdmin ? (
                            <button
                              type="button"
                              className="btn btn-green btn-sm"
                              onClick={() => setPaymentModalDriver(d)}
                              style={{ fontWeight: 700 }}
                            >
                              💰 Collect Dues
                            </button>
                          ) : (
                            <span className="badge red" style={{ opacity: 0.8, fontSize: 11 }}>Pending</span>
                          )
                        ) : (
                          <span className="badge green" style={{ opacity: 0.8, fontSize: 11 }}>✓ Up to date</span>
                        )}
                        {isSuperAdmin && canHoldForDues(d) && (
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            style={{ color: '#b45309', borderColor: 'rgba(180, 83, 9, 0.4)' }}
                            disabled={busy !== null}
                            onClick={() => controlDuesHold(d, 'hold')}
                            title="No new rides or trips until he pays. He can still use the app to pay."
                          >
                            {busy === `hold:${d.id}` ? t('Updating…') : `⏸ ${t('Hold for dues')}`}
                          </button>
                        )}
                        {isSuperAdmin && isDuesHeld(d) && (
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            style={{ color: '#0f766e', borderColor: 'rgba(13, 148, 136, 0.35)' }}
                            disabled={busy !== null}
                            onClick={() => controlDuesHold(d, 'release')}
                            title="Let him go online and take rides again, even though he still owes"
                          >
                            {busy === `hold:${d.id}` ? t('Updating…') : t('Release hold')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {ledgerData.list.length === 0 && (
                  <tr>
                    <td colSpan={9} className="empty">No captains match the selected filter or search query.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 2: Recent Receipts & Collections */}
      {activeTab === 'receipts' && (
        <>
          <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', marginBottom: 16 }}>
            <div className="stat-card green">
              <div className="stat-label">TODAY'S COLLECTIONS</div>
              <div className="stat-value green">₹{receiptsData.todayCollected}</div>
              <div className="stat-sub">{receiptsData.todayCount} dues receipts recorded today</div>
            </div>
            <div className="stat-card blue">
              <div className="stat-label">TOTAL ALL-TIME RECEIPTS</div>
              <div className="stat-value blue">₹{receiptsData.totalCollected}</div>
              <div className="stat-sub">{receiptsData.totalCount} historical payments on record</div>
            </div>
            <div className="stat-card red">
              <div className="stat-label">ACTIVE FLEET DUES REMAINING</div>
              <div className="stat-value red">₹{periodData.totalPlatformDuesOwed}</div>
              <div className="stat-sub">Across {ledgerData.pendingCount} captains with pending balance</div>
            </div>
          </div>

          <div className="card">
            <div className="card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <span>🧾 Dues Collection Receipts (Audit Log)</span>
                <span className="muted" style={{ display: 'block', fontSize: 12, marginTop: 2, fontWeight: 400 }}>
                  Real-time immutable receipts stream · Cash &amp; UPI platform commission records
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Search receipt, captain, vehicle, admin…"
                  value={receiptsQuery}
                  onChange={(e) => setReceiptsQuery(e.target.value)}
                  style={{ padding: '7px 12px', fontSize: 13, minWidth: 240 }}
                />
                <button type="button" className="btn btn-ghost btn-sm" onClick={exportReceiptsCsv}>
                  Export Receipts CSV
                </button>
              </div>
            </div>

            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>DATE &amp; TIME</th>
                    <th>CAPTAIN</th>
                    <th>AMOUNT RECEIVED</th>
                    <th>BALANCE (BEFORE ➔ AFTER)</th>
                    <th>METHOD</th>
                    <th>COLLECTED BY</th>
                    <th>NOTE / REF</th>
                  </tr>
                </thead>
                <tbody>
                  {receiptsData.list.map((p) => (
                    <tr key={p.id}>
                      <td className="mono" style={{ fontSize: 12 }}>{formatTime(p.paidAt)}</td>
                      <td>
                        <div style={{ fontWeight: 700 }}>{p.driverName || 'Anna'}</div>
                        <div className="mono muted" style={{ fontSize: 11 }}>{p.vehicleNumber || '—'} · {p.driverPhone || ''}</div>
                      </td>
                      <td className="mono">
                        <span style={{ color: '#34d399', fontWeight: 800, fontSize: 14 }}>
                          +₹{p.amount}
                        </span>
                      </td>
                      <td className="mono" style={{ fontSize: 12 }}>
                        {p.balanceBefore != null && p.balanceAfter != null ? (
                          <span>
                            <span style={{ color: '#f87171' }}>₹{p.balanceBefore}</span>
                            {' ➔ '}
                            <span style={{ color: p.balanceAfter === 0 ? '#34d399' : '#fbbf24', fontWeight: 700 }}>
                              ₹{p.balanceAfter}
                            </span>
                          </span>
                        ) : '—'}
                      </td>
                      <td>
                        <span className={`badge ${p.method === 'upi' ? 'blue' : 'green'}`} style={{ textTransform: 'uppercase', fontSize: 10 }}>
                          {p.method === 'upi' ? '📱 UPI' : '💵 Cash'}
                        </span>
                      </td>
                      <td className="mono muted" style={{ fontSize: 11 }}>
                        {p.adminEmail || p.adminUid || 'Admin'}
                      </td>
                      <td style={{ fontSize: 12, color: p.notes ? '#f8fafc' : '#64748b' }}>
                        {p.notes || '—'}
                      </td>
                    </tr>
                  ))}
                  {receiptsData.list.length === 0 && (
                    <tr>
                      <td colSpan={7} className="empty">No dues payment receipts found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Breakdown by Captain */}
      <div className="card">
        <div className="card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Breakdown by Captain · {periodLabel}</span>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => downloadCsv(`cash-breakdown-${periodFileTag}-${new Date().toISOString().slice(0, 10)}.csv`, [
              ['captain', 'vehicle', 'rides', 'fare', 'tips'],
              ...periodData.rows.map((r) => [r.name, r.vehicle, r.rides, r.fare, r.tips]),
            ])}
          >
            Export CSV
          </button>
        </div>
        {periodData.rows.length === 0 ? (
          <div className="empty">No completed rides for {periodLabel.toLowerCase()}</div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>CAPTAIN</th>
                  <th>VEHICLE</th>
                  <th>RIDES</th>
                  <th>FARE</th>
                  <th>TIPS</th>
                </tr>
              </thead>
              <tbody>
                {periodData.rows.map((r) => (
                  <tr key={r.id}>
                    <td><strong>{r.name}</strong></td>
                    <td className="mono">{r.vehicle}</td>
                    <td className="mono">{r.rides}</td>
                    <td className="mono">₹{r.fare}</td>
                    <td className="mono">₹{r.tips}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Completed Trips Log */}
      <div className="card">
        <div className="card-title">Completed Trips Log · {periodLabel}</div>
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>COMPLETED TIME</th>
                <th>ROUTE</th>
                <th>CAPTAIN</th>
                <th>FARE</th>
              </tr>
            </thead>
            <tbody>
              {periodData.completed.slice(0, 50).map((r) => (
                <tr key={r.id}>
                  <td className="mono">{formatTime(r.completedAt || r.updatedAt || r.createdAt)}</td>
                  <td>
                    <div className="muted">{r.pickup?.address}</div>
                    <div>→ {r.destination?.name || '—'}</div>
                  </td>
                  <td>{r.driverName || '—'}</td>
                  <td className="mono"><strong>₹{r.fare}</strong></td>
                </tr>
              ))}
              {periodData.completed.length === 0 && (
                <tr>
                  <td colSpan={4} className="empty">No completed trip logs for {periodLabel.toLowerCase()}.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {breakdownDriverId && (() => {
        const d = ledgerData.all.find((x) => x.id === breakdownDriverId);
        return d ? <DuesBreakdownModal driver={d} breakdown={d.breakdown} allRides={rides} onClose={() => setBreakdownDriverId(null)} /> : null;
      })()}

      {/* Dedicated Interactive Payment Collection Modal */}
      {paymentModalDriver && (
        <RecordPaymentModal
          driver={paymentModalDriver}
          onClose={() => setPaymentModalDriver(null)}
          clearDriverPendingDues={clearDriverPendingDues}
        />
      )}
    </>
  );
}
